import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { CheckCircle2, Server, Shield, Cloud, HeadphonesIcon, Database, Network, Lock, Sparkles } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// String → lucide icon map (admin stores icon as a string).
const ICON_MAP = {
  Server, Shield, Cloud, Database, Network, Lock, Sparkles,
  HeadphonesIcon,
  Headphones: HeadphonesIcon,
};

const fmtUSD = (n) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(n) || 0);

/**
 * Shared service detail + pricing-config modal.
 *
 * Used by:
 *   - public Services page (mode='quote') — collects contact info, posts to
 *     /api/services/:id/request-quote, creates a draft Quote in the admin queue
 *   - logged-in client portal (mode='subscribe') — posts to /api/subscriptions
 *     with the configured tier_id / device_count / addon_ids
 *
 * Live pricing is computed client-side via priceBreakdown() — purely a UX win
 * (instant feedback). The server recomputes server-side as the source of truth.
 */
const ServiceDetailDialog = ({ service, open, onClose, mode = 'quote', onSuccess }) => {
  const [tierId, setTierId] = useState(null);
  const [deviceCount, setDeviceCount] = useState(1);
  const [addonIds, setAddonIds] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [contact, setContact] = useState({ client_name: '', client_email: '', client_phone: '', notes: '' });

  // Reset state when a different service is opened.
  useEffect(() => {
    if (!service) return;
    if (service.pricing_type === 'tiered' && service.tiers?.length) {
      setTierId(service.tiers[0].tier_id);
    } else {
      setTierId(null);
    }
    setDeviceCount(1);
    setAddonIds([]);
    setContact({ client_name: '', client_email: '', client_phone: '', notes: '' });
  }, [service?.service_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const breakdown = useMemo(() => {
    if (!service) return null;
    const pt = service.pricing_type || 'flat';
    let baseLabel = '';
    let baseAmount = 0;

    if (pt === 'tiered' && service.tiers?.length) {
      const t = service.tiers.find((x) => x.tier_id === tierId) || service.tiers[0];
      baseLabel = `${service.name} — ${t.name}`;
      baseAmount = Number(t.price) || 0;
    } else if (pt === 'per_device') {
      const base = Number(service.price) || 0;
      const per = Number(service.per_device_price) || 0;
      const n = Math.max(parseInt(deviceCount || 0, 10), 0);
      baseLabel = base
        ? `${service.name} — base + ${n} device(s) × ${fmtUSD(per)}`
        : `${service.name} — ${n} device(s) × ${fmtUSD(per)}`;
      baseAmount = base + per * n;
    } else {
      baseLabel = service.name;
      baseAmount = Number(service.price) || 0;
    }

    const selectedAddons = (service.addons || []).filter((a) => addonIds.includes(a.addon_id));
    const monthlyAddons = selectedAddons.filter((a) => (a.charge_type || 'monthly') === 'monthly');
    const oneTimeAddons = selectedAddons.filter((a) => a.charge_type === 'one_time');
    const monthlyAddonsTotal = monthlyAddons.reduce((s, a) => s + (Number(a.price) || 0), 0);
    const oneTimeTotal = oneTimeAddons.reduce((s, a) => s + (Number(a.price) || 0), 0);

    return {
      baseLabel,
      baseAmount,
      monthlyAddons,
      oneTimeAddons,
      monthlyAddonsTotal,
      oneTimeTotal,
      monthlyRecurring: baseAmount + monthlyAddonsTotal,
    };
  }, [service, tierId, deviceCount, addonIds]);

  if (!service) return null;
  const Icon = ICON_MAP[service.icon] || Server;

  const toggleAddon = (id) => {
    setAddonIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const submit = async () => {
    if (mode === 'quote') {
      if (!contact.client_name.trim() || !contact.client_email.trim()) {
        toast.error('Please enter your name and email');
        return;
      }
    }
    setSubmitting(true);
    try {
      if (mode === 'quote') {
        const res = await axios.post(`${BACKEND_URL}/api/services/${service.service_id}/request-quote`, {
          tier_id: tierId,
          device_count: parseInt(deviceCount || 1, 10),
          addon_ids: addonIds,
          ...contact,
        });
        toast.success(res.data.message || 'Quote request received!');
        onSuccess?.(res.data);
        onClose?.();
      } else {
        const res = await axios.post(
          `${BACKEND_URL}/api/subscriptions`,
          {
            service_id: service.service_id,
            tier_id: tierId,
            device_count: parseInt(deviceCount || 1, 10),
            addon_ids: addonIds,
          },
          { withCredentials: true }
        );
        toast.success('Subscription activated');
        onSuccess?.(res.data);
        onClose?.();
      }
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : (e.message || 'Request failed');
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const pt = service.pricing_type || 'flat';

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="bg-card border-border max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="service-detail-dialog">
        <DialogHeader>
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
              <Icon className="w-6 h-6 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <DialogTitle className="text-2xl font-black uppercase tracking-tight">{service.name}</DialogTitle>
              <DialogDescription className="text-muted-foreground mt-1">{service.description}</DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Features */}
          {service.features?.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {service.features.map((f, i) => (
                <div key={i} className="flex items-start gap-2 text-sm">
                  <CheckCircle2 className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                  <span>{f}</span>
                </div>
              ))}
            </div>
          )}

          {/* Pricing config */}
          <div className="space-y-4 p-4 rounded-lg bg-muted/20 border border-border/40">
            <p className="text-xs uppercase tracking-widest text-muted-foreground">Configure your plan</p>

            {pt === 'tiered' && service.tiers?.length > 0 && (
              <div className="space-y-2" data-testid="tier-picker">
                <Label className="text-sm">Plan</Label>
                <div className="grid gap-2">
                  {service.tiers.map((t) => {
                    const checked = tierId === t.tier_id;
                    return (
                      <button
                        type="button"
                        key={t.tier_id}
                        onClick={() => setTierId(t.tier_id)}
                        data-testid={`tier-${t.tier_id}`}
                        className={`text-left p-3 rounded border transition-colors ${
                          checked ? 'border-primary bg-primary/10' : 'border-border hover:border-primary/50'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-bold">{t.name}</p>
                            <p className="text-xs text-muted-foreground">
                              {t.min_devices}–{t.max_devices ?? '∞'} devices
                            </p>
                          </div>
                          <p className="font-mono font-bold text-primary">{fmtUSD(t.price)}<span className="text-xs text-muted-foreground">/mo</span></p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {pt === 'per_device' && (
              <div className="space-y-2" data-testid="device-count-picker">
                <Label htmlFor="device-count" className="text-sm">
                  Number of devices
                  <span className="ml-2 text-xs text-muted-foreground">
                    (base {fmtUSD(service.price)} + {fmtUSD(service.per_device_price)} per device)
                  </span>
                </Label>
                <Input
                  id="device-count"
                  type="number"
                  min={1}
                  value={deviceCount}
                  onChange={(e) => setDeviceCount(e.target.value)}
                  className="w-32 bg-input"
                  data-testid="device-count-input"
                />
              </div>
            )}

            {service.addons?.length > 0 && (
              <div className="space-y-2" data-testid="addons-picker">
                <Label className="text-sm">Add-ons</Label>
                <div className="grid gap-2">
                  {service.addons.map((a) => (
                    <label
                      key={a.addon_id}
                      htmlFor={`addon-${a.addon_id}`}
                      className="flex items-start gap-3 p-3 rounded border border-border hover:border-primary/50 cursor-pointer transition-colors"
                    >
                      <Checkbox
                        id={`addon-${a.addon_id}`}
                        checked={addonIds.includes(a.addon_id)}
                        onCheckedChange={() => toggleAddon(a.addon_id)}
                        data-testid={`addon-checkbox-${a.addon_id}`}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-3">
                          <p className="text-sm font-medium">{a.name}</p>
                          <p className="text-sm font-mono text-primary whitespace-nowrap">
                            {fmtUSD(a.price)}
                            <span className="text-[10px] text-muted-foreground ml-1">
                              {a.charge_type === 'one_time' ? 'one-time' : '/mo'}
                            </span>
                          </p>
                        </div>
                        {a.description && (
                          <p className="text-xs text-muted-foreground mt-0.5">{a.description}</p>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Live breakdown */}
          {breakdown && (
            <div className="p-4 rounded-lg bg-card border border-primary/30" data-testid="price-breakdown">
              <div className="flex items-center justify-between text-sm">
                <span>{breakdown.baseLabel}</span>
                <span className="font-mono">{fmtUSD(breakdown.baseAmount)}/mo</span>
              </div>
              {breakdown.monthlyAddons.map((a) => (
                <div key={a.addon_id} className="flex items-center justify-between text-sm text-muted-foreground">
                  <span>+ {a.name}</span>
                  <span className="font-mono">{fmtUSD(a.price)}/mo</span>
                </div>
              ))}
              <div className="border-t border-border mt-2 pt-2 flex items-center justify-between">
                <span className="text-sm font-bold uppercase tracking-widest">Monthly</span>
                <span className="text-2xl font-black text-primary font-mono" data-testid="price-monthly-total">
                  {fmtUSD(breakdown.monthlyRecurring)}
                </span>
              </div>
              {breakdown.oneTimeTotal > 0 && (
                <>
                  {breakdown.oneTimeAddons.map((a) => (
                    <div key={a.addon_id} className="flex items-center justify-between text-xs text-muted-foreground mt-1">
                      <span>+ {a.name} <Badge variant="outline" className="text-[9px] ml-1">one-time</Badge></span>
                      <span className="font-mono">{fmtUSD(a.price)}</span>
                    </div>
                  ))}
                  <div className="border-t border-border/40 mt-2 pt-2 flex items-center justify-between">
                    <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">One-time setup</span>
                    <span className="text-sm font-mono font-bold" data-testid="price-onetime-total">
                      {fmtUSD(breakdown.oneTimeTotal)}
                    </span>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Contact form (quote mode only) */}
          {mode === 'quote' && (
            <div className="space-y-3 p-4 rounded-lg bg-muted/20 border border-border/40" data-testid="quote-contact-form">
              <p className="text-xs uppercase tracking-widest text-muted-foreground">Your contact info</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="qc-name" className="text-xs">Name</Label>
                  <Input
                    id="qc-name"
                    value={contact.client_name}
                    onChange={(e) => setContact({ ...contact, client_name: e.target.value })}
                    className="bg-input"
                    data-testid="quote-client-name"
                  />
                </div>
                <div>
                  <Label htmlFor="qc-email" className="text-xs">Email</Label>
                  <Input
                    id="qc-email"
                    type="email"
                    value={contact.client_email}
                    onChange={(e) => setContact({ ...contact, client_email: e.target.value })}
                    className="bg-input"
                    data-testid="quote-client-email"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="qc-phone" className="text-xs">Phone (optional)</Label>
                <Input
                  id="qc-phone"
                  value={contact.client_phone}
                  onChange={(e) => setContact({ ...contact, client_phone: e.target.value })}
                  className="bg-input"
                  data-testid="quote-client-phone"
                />
              </div>
              <div>
                <Label htmlFor="qc-notes" className="text-xs">Notes (optional)</Label>
                <Textarea
                  id="qc-notes"
                  rows={2}
                  value={contact.notes}
                  onChange={(e) => setContact({ ...contact, notes: e.target.value })}
                  className="bg-input"
                  data-testid="quote-notes"
                />
              </div>
            </div>
          )}

          <Button
            className="w-full"
            disabled={submitting}
            onClick={submit}
            data-testid="service-detail-submit-btn"
          >
            {submitting
              ? 'Submitting...'
              : mode === 'quote'
                ? 'REQUEST QUOTE'
                : 'SUBSCRIBE NOW'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ServiceDetailDialog;
